import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { catalogBySlug as buildCatalogBySlug } from "@heraion/shared";
import type { Exercise } from "@heraion/shared";

import { useLocale } from "../primitives/useLocale";
import {
  applyLibraryFilters,
  collectLibraryFilterOptions,
  emptyLibraryFilters,
  type LibraryTagFilters,
} from "./libraryFilters";
import {
  filterLibraryItems,
  useLibrary,
  type LibraryFilter,
  type LibraryItem,
} from "./useLibrary";

// The Library browser's derivation layer (split out of LibraryBrowser.tsx
// to keep that file layout+hosting only — see LibraryResults.tsx for the
// render half): filter-tab state, tag-filter state, the section grouping
// and the loading/empty/error booleans the browser's render branches on.
// Zero JSX, zero fetch — everything here is pure derivation over the data
// useLibrary already fetched.

export type Section = { key: string; label: string; items: LibraryItem[] };

export function useLibraryBrowse({
  lib,
  callerSub,
  catalog,
  lockedFilter,
  filter: controlledFilter,
  onFilterChange,
  tagFilters: controlledTagFilters,
  onTagFiltersChange,
}: {
  lib: ReturnType<typeof useLibrary>;
  callerSub: string | null;
  catalog: Exercise[] | null;
  /** CO-23 — the coach's program picker fixes the tab to "personal" (only
   *  what the coach created themself is theirs to (re)assign — see
   *  ProgramPickerSheet.tsx). Present ⇒ `filter` never leaves this value and
   *  `onPickFilter` is inert; absent (every other caller) ⇒ the normal
   *  tab-switching behaviour below, unchanged. Two callsites (LibraryBrowser
   *  passes nothing, ProgramPickerSheet passes "personal"), so this is a
   *  parameter, not a new abstraction. */
  lockedFilter?: LibraryFilter;
  /** MX-8b: optional controlled tab + tag filters, lifted above the
   *  Create-hub (useCreateHubWeb / useCreateHubNav) so a wide/narrow resize
   *  while the hub is open does not reset the search. Omit for this hook's
   *  own uncontrolled state (unchanged: ProgramPickerSheet, CoachLibraryScreen). */
  filter?: LibraryFilter;
  onFilterChange?: (f: LibraryFilter) => void;
  tagFilters?: LibraryTagFilters;
  // Dispatch shape (not a plain setter): onPickFilter below resets tag
  // filters through a functional update (`prev => ...`), so a controlled
  // caller must accept that form too; a raw useState setter already does.
  onTagFiltersChange?: Dispatch<SetStateAction<LibraryTagFilters>>;
}) {
  const { t } = useTranslation();
  const lang = useLocale();
  const [internalFilter, setInternalFilter] = useState<LibraryFilter>(
    lockedFilter ?? "programs",
  );
  const [internalTagFilters, setInternalTagFilters] =
    useState<LibraryTagFilters>(emptyLibraryFilters);
  const filter = controlledFilter ?? internalFilter;
  const setFilter = onFilterChange ?? setInternalFilter;
  const tagFilters = controlledTagFilters ?? internalTagFilters;
  const setTagFilters = onTagFiltersChange ?? setInternalTagFilters;
  const activeFilterSet = useMemo(() => new Set([filter]), [filter]);

  const catalogBySlug = useMemo(
    () => buildCatalogBySlug(catalog ?? []),
    [catalog],
  );
  // Options scoped to the ACTIVE tab, so an axis that can never match here
  // (Level on "Mine" — personal rows carry no difficulty; Sessions/week on
  // "Workouts" — plans have no frequency) simply doesn't render, instead of
  // offering chips that guarantee zero results (UX audit S3c).
  const filterOptions = useMemo(
    () =>
      collectLibraryFilterOptions(
        filterLibraryItems(
          lib.items,
          filter,
          lib.favoriteKeys,
          callerSub,
          lib.authoredAs,
        ),
        catalogBySlug,
      ),
    [lib.items, filter, lib.favoriteKeys, catalogBySlug, callerSub, lib.authoredAs],
  );

  const onPickFilter = (f: LibraryFilter) => {
    // Locked (the picker) ⇒ inert: there is no other tab to switch to.
    if (lockedFilter) return;
    // Stale "Cloned X" / error banners never outlive their tab.
    lib.clearFeedback();
    // Axis selections reset on tab switch — options are tab-scoped, so a
    // selection could otherwise keep filtering through an axis the new tab
    // doesn't even render. The typed query survives (its field stays visible).
    setTagFilters((prev) => ({ ...emptyLibraryFilters, query: prev.query }));
    setFilter(f);
  };

  const filterLabel = (f: LibraryFilter): string =>
    f === "favorites"
      ? t("library.tabFavorites", { defaultValue: "Favorites" })
      : f === "programs"
        ? t("library.tabPrograms", { defaultValue: "Programs" })
        : f === "workouts"
          ? t("library.tabWorkouts", { defaultValue: "Workouts" })
          : f === "personal"
            ? t("library.tabPersonal", { defaultValue: "Mine" })
            : t("library.tabCoach", { defaultValue: "Coach" });

  const sections = useMemo<Section[]>(() => {
    const visible = applyLibraryFilters(
      filterLibraryItems(
        lib.items,
        filter,
        lib.favoriteKeys,
        callerSub,
        lib.authoredAs,
      ),
      tagFilters,
      catalogBySlug,
      lang,
    );
    if (filter === "personal" || filter === "favorites" || filter === "coach") {
      return [
        {
          key: "programs",
          label: t("library.tabPrograms", { defaultValue: "Programs" }),
          items: visible.filter((i) => i.kind === "program"),
        },
        {
          key: "workouts",
          label: t("library.tabWorkouts", { defaultValue: "Workouts" }),
          items: visible.filter((i) => i.kind === "plan"),
        },
      ];
    }
    return [
      {
        key: "official",
        label: t("library.sectionOfficial", { defaultValue: "By $t(brand.product)" }),
        items: visible.filter((i) => i.source === "official"),
      },
      {
        key: "personal",
        label: t("library.sectionPersonal", { defaultValue: "Mine" }),
        items: visible.filter((i) => i.source === "personal"),
      },
    ];
    // `lang` included: search matches the RESOLVED name, so the visible
    // set has to recompute when the reader's language changes.
  }, [
    lib.items,
    filter,
    tagFilters,
    catalogBySlug,
    lib.favoriteKeys,
    callerSub,
    lib.authoredAs,
    t,
    lang,
  ]);

  const hasItems = sections.some((s) => s.items.length > 0);
  // Empty because the search/tag filters excluded everything (the tab has
  // data) — a different message than "you have nothing yet".
  const filteredOut =
    !hasItems &&
    filterLibraryItems(
      lib.items,
      filter,
      lib.favoriteKeys,
      callerSub,
      lib.authoredAs,
    ).length > 0;
  const loading =
    !hasItems &&
    (lib.templatesStatus === "loading" ||
      lib.personalStatus === "loading" ||
      lib.personalStatus === "idle" ||
      lib.plansStatus === "loading" ||
      lib.plansStatus === "idle" ||
      (filter === "favorites" && lib.favoritesStatus === "loading") ||
      // PE-48 review fix #8 — on mobile callerSub resolves async. Without
      // this, programs/plans landing before callerSub does made the coach
      // tab's own filter (which needs callerSub to tell "assigned to me"
      // apart from "mine") yield [] for a beat: a flash of "Nothing
      // assigned yet" before the real rows appear. Scoped to the coach tab
      // only — the other tabs don't read callerSub in their predicate.
      (filter === "coach" && callerSub === null));
  const loadFailed =
    !hasItems &&
    !loading &&
    (lib.templatesStatus === "error" ||
      lib.personalStatus === "error" ||
      lib.plansStatus === "error" ||
      (filter === "favorites" && lib.favoritesStatus === "error"));

  return {
    filter,
    activeFilterSet,
    tagFilters,
    setTagFilters,
    onPickFilter,
    filterLabel,
    filterOptions,
    sections,
    hasItems,
    filteredOut,
    loading,
    loadFailed,
  };
}
